/*
 * Copyright 2026 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 */
package studio.onboarding

import kotlinx.serialization.Serializable
import models.GoalCategory
import studio.AgeBand
import studio.HomeOwnership
import studio.IncomeInputMode
import studio.InflowSource
import studio.MarginalRateCalculator
import studio.PayFrequency
import studio.ProvinceCode
import studio.Region
import studio.RoomKnowledge
import studio.SavingsTenure
import studio.UserTaxContext
import studio.Wiz3rUserProfile

/**
 * Goal Allocation Studio onboarding state machine. Pure (no I/O).
 *
 * The platform UIs each render one [OnboardingStep] at a time. After each
 * answer they call [advance] which returns the next step (or `null` when the
 * stepper is complete). [progressOf] returns "x of 7" for the header chip.
 *
 * The stepper is **resilient to skips** — every question is skippable. The
 * resulting [PartialWiz3rUserProfile] may have unset fields; [toComplete]
 * turns it into a [Wiz3rUserProfile] only when the math-essential trio
 * (region, province, income) is set. The platform UI is responsible for the
 * dispatch — if those three aren't set, the Studio falls back to the §6.1
 * bracket-visualization input flow (PR3).
 */
@Serializable
enum class OnboardingStep {
    INCOME,
    AGE,
    HOME_OWNERSHIP,
    RRSP_ROOM,
    TFSA_ROOM,
    SAVINGS_TENURE,
    GOAL_PRIORITIES,
}

/**
 * In-progress profile. Every field is optional so the user can answer in any
 * order and skip freely. The platform UI persists this to local state
 * between cards.
 */
@Serializable
data class PartialWiz3rUserProfile(
    val region: Region? = null,
    val province: ProvinceCode? = null,
    val grossAnnualIncome: Double? = null,
    val incomeInputMode: IncomeInputMode = IncomeInputMode.GROSS_ANNUAL,
    /** Net per period when [incomeInputMode] == AVERAGE_PAYCHEQUE. */
    val netPerPeriod: Double? = null,
    val payFrequency: PayFrequency = PayFrequency.MONTHLY,
    val ageBand: AgeBand? = null,
    val homeOwnershipHistory: HomeOwnership? = null,
    val rrspRoomKnowledge: RoomKnowledge? = null,
    val rrspRoomAmount: Double? = null,
    val tfsaRoomKnowledge: RoomKnowledge? = null,
    val tfsaRoomAmount: Double? = null,
    val savingsTenure: SavingsTenure? = null,
    val goalPriorities: List<GoalCategory> = emptyList(),
)

object OnboardingFlow {

    val ALL_STEPS: List<OnboardingStep> = OnboardingStep.entries

    /**
     * Advance to the next step. Returns `null` when there are no more steps
     * (the stepper is complete and the caller should persist).
     */
    fun advance(current: OnboardingStep): OnboardingStep? {
        val idx = ALL_STEPS.indexOf(current)
        return ALL_STEPS.getOrNull(idx + 1)
    }

    /** Step back one card; returns `null` when the user is on the first step. */
    fun back(current: OnboardingStep): OnboardingStep? {
        val idx = ALL_STEPS.indexOf(current)
        return ALL_STEPS.getOrNull(idx - 1)
    }

    /** 1-indexed position within the flow. Used for the "x of 7" chip. */
    fun progressOf(step: OnboardingStep): Int = ALL_STEPS.indexOf(step) + 1

    /** Total step count. */
    fun totalSteps(): Int = ALL_STEPS.size

    /**
     * Derive `grossAnnualIncome` from the user's chosen input mode. Returns
     * null when not enough has been answered yet.
     *
     * For [IncomeInputMode.GROSS_ANNUAL]: pass-through.
     *
     * For [IncomeInputMode.AVERAGE_PAYCHEQUE]: gross-up via iterative tax
     * solve so the resulting gross, after federal + provincial tax, yields
     * the user's stated net. Phase 1 uses a simplified ratio of 0.72 (i.e.
     * net ≈ gross × 0.72 for typical CA incomes). PR3 will swap in the
     * iterative solver from §14.2.1 once the marginal-rate engine is
     * extended to support reverse lookup.
     */
    fun resolveGrossIncome(partial: PartialWiz3rUserProfile): Double? = when (partial.incomeInputMode) {
        IncomeInputMode.GROSS_ANNUAL -> partial.grossAnnualIncome
        IncomeInputMode.AVERAGE_PAYCHEQUE -> {
            val net = partial.netPerPeriod ?: return null
            val annualizedNet = when (partial.payFrequency) {
                PayFrequency.MONTHLY -> net * 12.0
                PayFrequency.BI_WEEKLY -> net * 26.0
                PayFrequency.ANNUAL -> net
            }
            // Simplified gross-up. The exact iterative solve lives in §14.2.1
            // for PR3 — for now this rough factor is close enough to feed
            // the marginal-rate calculator at typical CA salaries.
            annualizedNet / 0.72
        }
        IncomeInputMode.BANK_PRE_FILL -> partial.grossAnnualIncome  // PR3
    }

    /**
     * Convert the in-progress profile to a complete [Wiz3rUserProfile] if and
     * only if the math-essential trio (region, province, income) is set.
     * Otherwise returns null — the caller renders the bracket-viz fallback.
     *
     * The marginal rate is computed via [MarginalRateCalculator] so the
     * stepper doesn't carry duplicate math.
     */
    fun toComplete(
        partial: PartialWiz3rUserProfile,
        taxYear: Int,
        updatedAt: String,
        inflowSources: List<InflowSource> = emptyList(),
    ): Wiz3rUserProfile? {
        val region = partial.region ?: Region.CA  // default to CA when not chosen
        val province = partial.province ?: return null
        val gross = resolveGrossIncome(partial) ?: return null
        val marginal = MarginalRateCalculator.marginalRate(gross, province, taxYear)
        val tax = UserTaxContext(
            region = region,
            province = province,
            grossAnnualIncome = gross,
            taxYear = taxYear,
            marginalRateNow = marginal,
            isFirstTimeHomeBuyer = partial.homeOwnershipHistory == HomeOwnership.NEVER_OWNED,
        )
        return Wiz3rUserProfile(
            tax = tax,
            ageBand = partial.ageBand ?: AgeBand.THIRTY_TO_THIRTYNINE,
            homeOwnershipHistory = partial.homeOwnershipHistory ?: HomeOwnership.DECLINED,
            rrspRoomKnowledge = partial.rrspRoomKnowledge ?: RoomKnowledge.UNKNOWN,
            rrspRoomAmount = partial.rrspRoomAmount,
            tfsaRoomKnowledge = partial.tfsaRoomKnowledge ?: RoomKnowledge.UNKNOWN,
            tfsaRoomAmount = partial.tfsaRoomAmount,
            savingsTenure = partial.savingsTenure ?: SavingsTenure.NEVER,
            goalPriorities = partial.goalPriorities,
            inflowSources = inflowSources,
            updatedAt = updatedAt,
        )
    }

    /**
     * Reverse: turn a saved profile into a PartialWiz3rUserProfile so the user
     * can resume edits where they left off. Used by the §14.6 "Profile" link
     * in the Studio header.
     */
    fun fromComplete(profile: Wiz3rUserProfile): PartialWiz3rUserProfile = PartialWiz3rUserProfile(
        region = profile.tax.region,
        province = profile.tax.province,
        grossAnnualIncome = profile.tax.grossAnnualIncome,
        ageBand = profile.ageBand,
        homeOwnershipHistory = profile.homeOwnershipHistory,
        rrspRoomKnowledge = profile.rrspRoomKnowledge,
        rrspRoomAmount = profile.rrspRoomAmount,
        tfsaRoomKnowledge = profile.tfsaRoomKnowledge,
        tfsaRoomAmount = profile.tfsaRoomAmount,
        savingsTenure = profile.savingsTenure,
        goalPriorities = profile.goalPriorities,
    )
}
